import React, { useState, useEffect } from "react";
import { Stack, Text } from "@fluentui/react";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import AddButton from "@/ui-controls/AddButton";
import ShimmerList from "@/ui-controls/ShimmerList";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";
import SetDefaultPanel from "@/components/integrations/customer-defaults/SetDefaultPanel";
import {
  CustomerDefaultIntegrationDto,
  IntegrationDto,
  IntegrationTagDto,
  listCustomerDefaultIntegrations,
  listIntegrations,
  listIntegrationTags,
} from "@/api/integrations";

const CustomerDefaultsPage = () => {
  const [defaults, setDefaults] = useState<
    CustomerDefaultIntegrationDto[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [customerId, setCustomerId] = useState<number | undefined>();
  const [isNewPanelOpen, setIsNewPanelOpen] = useState(false);
  const [tagMap, setTagMap] = useState<Record<number, string>>({});
  const [integrationMap, setIntegrationMap] = useState<Record<number, string>>({});

  useEffect(() => {
    listIntegrationTags().then((data) => {
      const map: Record<number, string> = {};
      (data?.result || []).forEach((t: IntegrationTagDto) => { map[t.id] = t.name; });
      setTagMap(map);
    });
    listIntegrations().then((data) => {
      const map: Record<number, string> = {};
      (data?.result || []).forEach((i: IntegrationDto) => { map[i.id] = i.name; });
      setIntegrationMap(map);
    });
  }, []);

  const loadData = () => {
    if (!customerId) {
      setDefaults([]);
      return;
    }
    setLoading(true);
    listCustomerDefaultIntegrations(customerId)
      .then((data) => setDefaults(data?.result || []))
      .catch(() => setDefaults([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [customerId]);

  const columns: DataGridColumn[] = [
    {
      key: "integrationTagId",
      name: "Category",
      minWidth: 120,
      maxWidth: 200,
      onRender: (item: CustomerDefaultIntegrationDto) =>
        tagMap[item.integrationTagId] || item.integrationTagId.toString(),
    },
    {
      key: "integration",
      name: "Default Integration",
      minWidth: 200,
      maxWidth: 350,
      onRender: (item: CustomerDefaultIntegrationDto) =>
        integrationMap[item.integrationId] || item.integrationId.toString(),
    },
    {
      key: "isActive",
      name: "Status",
      fieldName: "isActive",
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: CustomerDefaultIntegrationDto) => (
        <Text
          styles={{
            root: {
              color: item.isActive ? "#107c10" : "#a80000",
              fontWeight: 600,
            },
          }}
        >
          {item.isActive ? "Active" : "Inactive"}
        </Text>
      ),
    },
  ];

  const handleSave = () => {
    setIsNewPanelOpen(false);
    loadData();
  };

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: "16px" } }}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
      >
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
          Customer Default Integrations
        </Text>
        <AddButton
          text="Set Default"
          onClick={() => setIsNewPanelOpen(true)}
        />
      </Stack>

      <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="end">
        <CustomerComboBox
          selectedCustomerId={customerId}
          onChange={(id) => setCustomerId(id)}
          placeholder="Filter by customer..."
        />
      </Stack>

      {loading ? (
        <ShimmerList numberOfColumns={3} />
      ) : (
        <DataGrid items={defaults} columns={columns} />
      )}

      <SetDefaultPanel
        isOpen={isNewPanelOpen}
        customerId={customerId ? String(customerId) : ""}
        onDismiss={() => setIsNewPanelOpen(false)}
        onSave={handleSave}
      />
    </Stack>
  );
};

export default CustomerDefaultsPage;
